import type { DocumentType } from "@freightsimple/generated-dashboard-openapi-client/models/DocumentType";
import { TrashIcon } from "@phosphor-icons/react/Trash";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Table } from "@freightsimple/ui-core/Table/Table";
import type { Column } from "@freightsimple/ui-core/Table/Table";
import type { ReactNode } from "react";
import { useCallback } from "react";
import ShipmentSection from "@/Shared/Shipment/CustomComponents/ShipmentSection";
import { useShipmentService } from "@/Services/ShipmentService";
import { ShipmentDocumentThumbnail } from "@/Shared/Shipment/CustomComponents/ShipmentDocumentThumbnail";
import { ShipmentDocumentDownloadButton } from "@/Shared/Shipment/CustomComponents/ShipmentDocumentDownloadButton";

export interface DocumentsSectionItem {
  documentId?: string | undefined;
  documentDescription: string;
  existsYet: boolean;
  creationDate: moment.Moment | undefined;
  onDownloadDocument: () => Promise<void>;
  /** Set only when the customer may remove this document (their own photo, before pickup). */
  onRemoveDocument?: (() => Promise<void>) | undefined;
  documentType: DocumentType;
}

interface DocumentsSectionGenericProps {
  children?: ReactNode | undefined;
  items: Array<DocumentsSectionItem>;
  shipmentId?: string | undefined;
  onReload?: (() => Promise<void>) | undefined;
  title: string | ReactNode;
  subtitle: string;
  sectionId: string;
  thumbnailType: "pdf" | "img";
  showCustomsButton?: boolean | undefined;
  onUpdateCustomDocs?: (() => void) | undefined;
}

function ShipmentDocumentsSectionGeneric(props: DocumentsSectionGenericProps) {
  const { items } = props;
  const { downloadThumbnail } = useShipmentService();

  const fetchThumbnail = useCallback(
    async (documentId: string) => {
      return await downloadThumbnail(documentId);
    },
    [downloadThumbnail],
  );

  const columns: Column<DocumentsSectionItem>[] = [
    {
      key: "document",
      header: "Document",
      render: (item) => (
        <div className="flex flex-row items-center gap-6 text-[18px] font-medium text-content-secondary">
          <ShipmentDocumentThumbnail
            documentId={item.documentId}
            fetchThumbnail={fetchThumbnail}
            type={props.thumbnailType}
            documentType={item.documentType}
            documentDescription={item.documentDescription}
            onDownloadDocument={item.onDownloadDocument}
            showThumbnail={item.existsYet}
          />
          {item.documentDescription}
        </div>
      ),
    },
    {
      key: "date",
      header: "Date",
      render: (item) => (
        <div className="text-base font-light text-content-muted">
          {item.existsYet
            ? item.creationDate?.format("Do MMMM YYYY")
            : "Not yet available"}
        </div>
      ),
    },
  ];

  return (
    <ShipmentSection
      title={props.title}
      subtitle={props.subtitle}
      id={props.sectionId}
    >
      <Table
        columns={columns}
        data={items}
        rowKey={(item) => item.documentId ?? item.documentDescription}
        rowActions={(item) =>
          item.existsYet && (
            <>
              <ShipmentDocumentDownloadButton
                onDownloadDocument={item.onDownloadDocument}
              />
              {item.onRemoveDocument && (
                <Button
                  icon={TrashIcon}
                  tone="danger"
                  onClick={item.onRemoveDocument}
                  confirm={{
                    title: `Remove ${item.documentDescription}?`,
                    message:
                      "It will no longer show on this shipment. Contact us if you need it back.",
                    confirmLabel: "Remove",
                  }}
                >
                  Remove
                </Button>
              )}
            </>
          )
        }
      />
    </ShipmentSection>
  );
}

export default ShipmentDocumentsSectionGeneric;
